import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Stethoscope, 
  HelpCircle, 
  ChevronDown,
  MessageCircle,
  Check,
  Users
} from 'lucide-react';
import { formatTime } from '../lib/utils';
import { getDeviceId } from '../lib/deviceId';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Single operatory column component - operatory name above buttons
function OperatoryColumn({ operatory, practice, fetchOperatories, sizing }) {
  const [patientState, setPatientState] = useState('empty');
  const [arrivedTime, setArrivedTime] = useState(null);
  const [seatedTime, setSeatedTime] = useState(null);
  const [arrivedElapsed, setArrivedElapsed] = useState(0);
  const [seatedElapsed, setSeatedElapsed] = useState(0);
  const [drNeededTime, setDrNeededTime] = useState(null);
  const [drNeededElapsed, setDrNeededElapsed] = useState(0);
  const [drNeededInRoom, setDrNeededInRoom] = useState(false);
  const [drInRoomTime, setDrInRoomTime] = useState(null);
  const [drInRoomElapsed, setDrInRoomElapsed] = useState(0);
  const [helpNeededTime, setHelpNeededTime] = useState(null);
  const [helpNeededElapsed, setHelpNeededElapsed] = useState(0);

  const drInRoomEnabled = practice?.dr_in_room_enabled || false;

  useEffect(() => {
    if (operatory) {
      setPatientState(operatory.patient_flow_state || 'empty');
      if (operatory.arrived_at) {
        const t = new Date(operatory.arrived_at);
        setArrivedTime(t);
        setArrivedElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      } else {
        setArrivedTime(null);
      }
      if (operatory.seated_at) {
        const t = new Date(operatory.seated_at);
        setSeatedTime(t);
        setSeatedElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      } else {
        setSeatedTime(null);
      }
      if (operatory.dr_needed_at) {
        const t = new Date(operatory.dr_needed_at);
        setDrNeededTime(t);
        setDrNeededElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      } else {
        setDrNeededTime(null);
      }
      setDrNeededInRoom(!!operatory.dr_needed_in_room_at);
      if (operatory.dr_needed_in_room_at) {
        const t = new Date(operatory.dr_needed_in_room_at);
        setDrInRoomTime(t);
        setDrInRoomElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      } else {
        setDrInRoomTime(null);
      }
      if (operatory.help_needed_at) {
        const t = new Date(operatory.help_needed_at);
        setHelpNeededTime(t);
        setHelpNeededElapsed(Math.floor((Date.now() - t.getTime()) / 1000));
      } else {
        setHelpNeededTime(null);
      }
    }
  }, [operatory]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (arrivedTime && (patientState === 'arrived' || patientState === 'arrived_with_next')) {
        setArrivedElapsed(Math.floor((Date.now() - arrivedTime.getTime()) / 1000));
      }
      if (seatedTime && ['seated', 'seated_with_next', 'dr_in_room', 'dr_in_room_with_next'].includes(patientState)) {
        setSeatedElapsed(Math.floor((Date.now() - seatedTime.getTime()) / 1000));
      }
      if (drNeededTime && !drNeededInRoom) {
        setDrNeededElapsed(Math.floor((Date.now() - drNeededTime.getTime()) / 1000));
      }
      if (drInRoomTime && drNeededInRoom) {
        setDrInRoomElapsed(Math.floor((Date.now() - drInRoomTime.getTime()) / 1000));
      }
      if (helpNeededTime) {
        setHelpNeededElapsed(Math.floor((Date.now() - helpNeededTime.getTime()) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [arrivedTime, seatedTime, drNeededTime, drInRoomTime, helpNeededTime, patientState, drNeededInRoom]);

  // Handlers — with optimistic state resets to prevent stale timer flash
  const handlePatientArrived = async () => {
    setPatientState('arrived');
    setArrivedTime(new Date());
    setArrivedElapsed(0);
    setSeatedTime(null);
    setSeatedElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/patient-arrived`, { device_id: getDeviceId() });
      toast.success(`Patient arrived - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to update status');
      fetchOperatories();
    }
  };

  const handlePatientSeated = async () => {
    setPatientState('seated');
    setSeatedTime(new Date());
    setSeatedElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/patient-seated`);
      toast.success(`Patient seated - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to update status');
      fetchOperatories();
    }
  };

  const handleDrInRoom = async () => {
    setPatientState('dr_in_room');
    try {
      await axios.post(`${API}/operatory/${operatory.id}/dr-in-room`);
      toast.success(`Doctor in room - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to update status');
      fetchOperatories();
    }
  };

  const handleCheckout = async () => {
    setPatientState('empty');
    setArrivedTime(null);
    setArrivedElapsed(0);
    setSeatedTime(null);
    setSeatedElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/checkout`);
      toast.success(`Patient checked out - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to checkout');
      fetchOperatories();
    }
  };

  const handleDrNeeded = async () => {
    setDrNeededTime(new Date());
    setDrNeededElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/dr-needed`, { doctor_name: 'Any Doctor', device_id: getDeviceId() });
      toast.success(`Doctor needed - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to request doctor');
      fetchOperatories();
    }
  };

  const handleDrNeededInRoom = async () => {
    setDrNeededInRoom(true);
    setDrInRoomTime(new Date());
    setDrInRoomElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/dr-needed-in-room`);
      toast.success(`Doctor in room - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to update status');
      fetchOperatories();
    }
  };

  const handleDrNeededComplete = async () => {
    setDrNeededTime(null);
    setDrNeededElapsed(0);
    setDrNeededInRoom(false);
    setDrInRoomTime(null);
    setDrInRoomElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/dr-needed-complete`);
      toast.success(`Doctor request complete - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to complete request');
      fetchOperatories();
    }
  };

  const handleClearDrNeeded = async () => {
    setDrNeededTime(null);
    setDrNeededElapsed(0);
    setDrNeededInRoom(false);
    setDrInRoomTime(null);
    setDrInRoomElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/clear-dr-needed`);
      toast.success(`Doctor request cleared - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to clear request');
      fetchOperatories();
    }
  };

  const handleHelpNeeded = async () => {
    setHelpNeededTime(new Date());
    setHelpNeededElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/help-needed`, { message: 'Help needed', device_id: getDeviceId() });
      toast.success(`Help requested - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to request help');
      fetchOperatories();
    }
  };

  const handleClearHelp = async () => {
    setHelpNeededTime(null);
    setHelpNeededElapsed(0);
    try {
      await axios.post(`${API}/operatory/${operatory.id}/clear-help`);
      toast.success(`Help request cleared - ${operatory.name}`);
      fetchOperatories();
    } catch (error) {
      toast.error('Failed to clear help');
      fetchOperatories();
    }
  };

  // Check if state has a "next patient" waiting
  const hasNext = patientState?.endsWith('_with_next');
  // Next patient timer
  const nextArrivedTime = operatory?.next_arrived_at ? new Date(operatory.next_arrived_at) : null;
  const [nextElapsed, setNextElapsed] = useState(0);
  useEffect(() => {
    if (!nextArrivedTime || !hasNext) return;
    const interval = setInterval(() => {
      setNextElapsed(Math.floor((Date.now() - nextArrivedTime.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [nextArrivedTime, hasNext]);

  const isDoctor = operatory?.current_provider_name?.startsWith('Dr.');
  const goToDrInRoom = drInRoomEnabled && isDoctor;

  const arrivedColor = practice?.button_color_patient_arrived || '#0284c7';
  const seatedColor = practice?.button_color_patient_seated || '#059669';
  const drNeededColor = practice?.button_color_dr_needed || '#f59e0b';
  const helpColor = practice?.button_color_help_needed || '#8b5cf6';
  const drInRoomColor = practice?.button_color_dr_in_room || '#4f46e5';

  // Height map for 2/3 sizing (normal → with_next)
  const mainHeightMap = { 'h-10': 'h-7', 'h-9': 'h-6', 'h-8': 'h-5', 'h-7': 'h-5' };
  const nextBarHeight = { 'h-10': 'h-2.5', 'h-9': 'h-2', 'h-8': 'h-1.5', 'h-7': 'h-1.5' };
  const mainBtnHeight = hasNext ? (mainHeightMap[sizing.buttonHeight] || 'h-7') : sizing.buttonHeight;
  const nextBarH = nextBarHeight[sizing.buttonHeight] || 'h-2';

  // Patient Flow Button
  const renderPatientFlowButton = () => {
    const wrapWithNext = (mainBtn, nextColor) => {
      if (!hasNext) return mainBtn;
      return (
        <div className={`flex flex-col items-center ${sizing.buttonWidth}`}>
          {mainBtn}
          <div
            data-testid={`widget-next-bar-${operatory.number}`}
            className={`${nextBarH} mt-0.5 w-full rounded-full`}
            style={{ backgroundColor: nextColor }}
          />
        </div>
      );
    };

    if (patientState === 'empty' || !patientState) {
      return (
        <Button onClick={handlePatientArrived} data-testid={`widget-arrived-${operatory.number}`}
          className={`${sizing.buttonHeight} ${sizing.buttonWidth} rounded-full text-white font-medium ${sizing.textSize} flex items-center justify-center gap-1 hover:brightness-110`} style={{ backgroundColor: arrivedColor }}>
          Arrived
        </Button>
      );
    }
    if (patientState === 'arrived' || patientState === 'arrived_with_next') {
      const mainBtn = (
        <Button onClick={handlePatientSeated} data-testid={`widget-seated-${operatory.number}`}
          className={`${mainBtnHeight} ${sizing.buttonWidth} rounded-full text-white font-medium ${sizing.textSize} flex items-center justify-center gap-1 hover:brightness-110`} style={{ backgroundColor: arrivedColor }}>
          <span>Arr</span>
          <span className={`font-mono ${sizing.timerSize}`}>{formatTime(arrivedElapsed)}</span>
        </Button>
      );
      return wrapWithNext(mainBtn, arrivedColor);
    }
    if (patientState === 'seated' || patientState === 'seated_with_next') {
      const mainBtn = (
        <Button onClick={goToDrInRoom ? handleDrInRoom : handleCheckout} data-testid={`widget-checkout-${operatory.number}`}
          className={`${mainBtnHeight} ${sizing.buttonWidth} rounded-full text-white font-medium ${sizing.textSize} flex items-center justify-center gap-1 hover:brightness-110`} style={{ backgroundColor: seatedColor }}>
          <span>Seated</span>
          <span className={`font-mono ${sizing.timerSize}`}>{formatTime(seatedElapsed)}</span>
        </Button>
      );
      return wrapWithNext(mainBtn, arrivedColor);
    }
    if (patientState === 'dr_in_room' || patientState === 'dr_in_room_with_next') {
      const mainBtn = (
        <Button onClick={handleCheckout} data-testid={`widget-drinroom-${operatory.number}`}
          className={`${mainBtnHeight} ${sizing.buttonWidth} rounded-full text-white font-medium ${sizing.textSize} flex items-center justify-center gap-1 hover:brightness-110`} style={{ backgroundColor: drInRoomColor }}>
          <Stethoscope className={sizing.iconSize} />
          <span className={`font-mono ${sizing.timerSize}`}>{formatTime(seatedElapsed)}</span>
        </Button>
      );
      return wrapWithNext(mainBtn, arrivedColor);
    }
    return (
      <Button onClick={handlePatientArrived} data-testid={`widget-arrived-${operatory.number}`}
        className={`${sizing.buttonHeight} ${sizing.buttonWidth} rounded-full text-white font-medium ${sizing.textSize} flex items-center justify-center gap-1 hover:brightness-110`} style={{ backgroundColor: arrivedColor }}>
        Arrived
      </Button>
    );
  };

  // Dr. Needed Button
  const btnClass = `${sizing.buttonHeight} ${sizing.buttonWidth} rounded-full text-white font-medium ${sizing.textSize} flex items-center justify-center gap-1`;
  const renderDrNeededButton = () => {
    if (drNeededInRoom) {
      return (
        <Button onClick={handleDrNeededComplete} data-testid={`widget-drneeded-${operatory.number}`}
          className={`${btnClass} hover:brightness-110`} style={{ backgroundColor: drInRoomColor }}>
          <Stethoscope className={sizing.iconSize} />
          <span className={`font-mono ${sizing.timerSize}`}>{formatTime(drInRoomElapsed)}</span>
        </Button>
      );
    }
    if (drNeededTime) {
      return (
        <Button onClick={drInRoomEnabled ? handleDrNeededInRoom : handleClearDrNeeded} data-testid={`widget-drneeded-${operatory.number}`}
          className={`${btnClass} hover:brightness-110`} style={{ backgroundColor: drNeededColor }}>
          <Stethoscope className={sizing.iconSize} />
          <span className={`font-mono ${sizing.timerSize}`}>{formatTime(drNeededElapsed)}</span>
        </Button>
      );
    }
    return (
      <Button onClick={handleDrNeeded} data-testid={`widget-drneeded-${operatory.number}`}
        className={`${btnClass} hover:brightness-110`} style={{ backgroundColor: drNeededColor }}>
        <Stethoscope className={sizing.iconSize} />
        <span>Dr.</span>
      </Button>
    );
  };

  // Help Needed Button
  const renderHelpNeededButton = () => {
    if (helpNeededTime) {
      return (
        <Button onClick={handleClearHelp} data-testid={`widget-help-${operatory.number}`}
          className={`${btnClass} hover:brightness-110`} style={{ backgroundColor: helpColor }}>
          <HelpCircle className={sizing.iconSize} />
          <span className={`font-mono ${sizing.timerSize}`}>{formatTime(helpNeededElapsed)}</span>
        </Button>
      );
    }
    return (
      <Button onClick={handleHelpNeeded} data-testid={`widget-help-${operatory.number}`}
        className={`${btnClass} hover:brightness-110`} style={{ backgroundColor: helpColor }}>
        <HelpCircle className={sizing.iconSize} />
        <span>Help</span>
      </Button>
    );
  };

  return (
    <div className="flex flex-col items-center" style={{ minWidth: sizing.columnMinWidth }}>
      {/* Operatory Name - directly above buttons */}
      <div className={`${sizing.operatoryLabelSize} text-slate-400 text-center mb-1`}>
        {operatory.name}
      </div>
      
      {/* 3 Buttons in horizontal row */}
      <div className={`flex items-center ${sizing.buttonGap}`}>
        {renderPatientFlowButton()}
        {renderDrNeededButton()}
        {renderHelpNeededButton()}
      </div>
    </div>
  );
}

export default function FloatingWidget({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const [practice, setPractice] = useState(null);
  const [providerOperatories, setProviderOperatories] = useState([]);
  const [providerName, setProviderName] = useState('');
  const [loading, setLoading] = useState(true);

  // Assistant provider selection state
  const isAssistant = user?.role === 'assistant' || user?.base_role === 'assistant';
  const [showProviderPicker, setShowProviderPicker] = useState(false);
  const [availableProviders, setAvailableProviders] = useState([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState(() => {
    try {
      const saved = sessionStorage.getItem('assistant_selected_providers');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Fetch providers for assistant selection
  const fetchProviders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/providers`);
      setAvailableProviders(res.data);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    }
  }, []);

  // Show provider picker when assistant opens widget with no selection — disabled, handled by DashboardPage
  useEffect(() => {
    if (isOpen && isAssistant) {
      fetchProviders();
    }
  }, [isOpen, isAssistant, fetchProviders]);

  const toggleProvider = (providerId) => {
    setSelectedProviderIds(prev => {
      const next = prev.includes(providerId)
        ? prev.filter(id => id !== providerId)
        : [...prev, providerId];
      sessionStorage.setItem('assistant_selected_providers', JSON.stringify(next));
      return next;
    });
  };

  const confirmProviderSelection = () => {
    if (selectedProviderIds.length === 0) {
      toast.error('Please select at least one provider');
      return;
    }
    setShowProviderPicker(false);
  };

  const fetchOperatories = useCallback(async () => {
    try {
      const practiceRes = await axios.get(`${API}/practice`);
      setPractice(practiceRes.data);

      const opsRes = await axios.get(`${API}/operatories`);
      const allOperatories = opsRes.data;

      const userId = user?.id;
      const userFullName = user?.full_name || '';
      
      if (!userId) {
        setProviderOperatories([]);
        setLoading(false);
        return;
      }

      // For assistants: show operatories of selected providers
      if (isAssistant) {
        // Read directly from sessionStorage to avoid stale state
        let providerIds = [];
        try {
          const saved = sessionStorage.getItem('assistant_selected_providers');
          if (saved) providerIds = JSON.parse(saved);
        } catch {}
        
        if (providerIds.length > 0) {
          const assistantOps = allOperatories.filter(op =>
            providerIds.includes(op.current_provider_id)
          );
          if (assistantOps.length > 0) {
            const names = [...new Set(assistantOps.map(op => op.current_provider_name).filter(Boolean))];
            setProviderName(names.join(', ') || user?.full_name || 'Assistant');
            setProviderOperatories(assistantOps.sort((a, b) => (a.number || 0) - (b.number || 0)));
          } else {
            setProviderName(user?.full_name || 'Assistant');
            setProviderOperatories([]);
          }
          setLoading(false);
          return;
        }
      }

      const myOperatories = allOperatories.filter(op => {
        if (op.current_provider_id === userId) return true;
        return false;
      });
      
      if (myOperatories.length > 0) {
        setProviderName(myOperatories[0].current_provider_name || user?.full_name || 'Provider');
        setProviderOperatories(myOperatories.sort((a, b) => (a.number || 0) - (b.number || 0)));
      } else {
        const deviceId = getDeviceId();
        const devicesRes = await axios.get(`${API}/devices`);
        const devices = devicesRes.data;
        const myDevice = devices.find(d => d.device_id === deviceId);
        
        if (myDevice?.operatory_id) {
          const registeredOp = allOperatories.find(op => op.id === myDevice.operatory_id);
          if (registeredOp) {
            setProviderName(registeredOp.current_provider_name || user?.full_name || 'Provider');
            setProviderOperatories([registeredOp]);
          } else {
            setProviderOperatories([]);
          }
        } else {
          setProviderOperatories([]);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch operatories:', error);
      setLoading(false);
    }
  }, [user, isAssistant, selectedProviderIds]);

  useEffect(() => {
    if (isOpen) {
      fetchOperatories();
      const interval = setInterval(fetchOperatories, 3000);
      return () => clearInterval(interval);
    }
  }, [isOpen, fetchOperatories]);

  if (!isOpen) return null;

  const opCount = providerOperatories.length || 1;
  
  // Dynamic sizing based on operatory count
  const getSizing = (count) => {
    if (count === 1) {
      return {
        widgetWidth: '340px',
        columnMinWidth: '280px',
        buttonHeight: 'h-10',
        buttonWidth: 'w-[85px]',
        buttonGap: 'gap-2',
        textSize: 'text-xs',
        timerSize: 'text-[10px]',
        iconSize: 'w-3.5 h-3.5',
        labelSize: 'text-xs',
        headerTextSize: 'text-sm',
        operatoryLabelSize: 'text-xs'
      };
    } else if (count === 2) {
      return {
        widgetWidth: '544px',
        columnMinWidth: '240px',
        buttonHeight: 'h-9',
        buttonWidth: 'w-[75px]',
        buttonGap: 'gap-1.5',
        textSize: 'text-[11px]',
        timerSize: 'text-[9px]',
        iconSize: 'w-3 h-3',
        labelSize: 'text-[11px]',
        headerTextSize: 'text-sm',
        operatoryLabelSize: 'text-[11px]'
      };
    } else if (count === 3) {
      return {
        widgetWidth: '760px',
        columnMinWidth: '210px',
        buttonHeight: 'h-8',
        buttonWidth: 'w-[68px]',
        buttonGap: 'gap-1',
        textSize: 'text-[10px]',
        timerSize: 'text-[8px]',
        iconSize: 'w-2.5 h-2.5',
        labelSize: 'text-[10px]',
        headerTextSize: 'text-xs',
        operatoryLabelSize: 'text-[10px]'
      };
    } else if (count === 4) {
      return {
        widgetWidth: '884px',
        columnMinWidth: '185px',
        buttonHeight: 'h-7',
        buttonWidth: 'w-[58px]',
        buttonGap: 'gap-1',
        textSize: 'text-[9px]',
        timerSize: 'text-[7px]',
        iconSize: 'w-2 h-2',
        labelSize: 'text-[9px]',
        headerTextSize: 'text-xs',
        operatoryLabelSize: 'text-[9px]'
      };
    } else {
      return {
        widgetWidth: '95vw',
        columnMinWidth: '160px',
        buttonHeight: 'h-6',
        buttonWidth: 'w-[50px]',
        buttonGap: 'gap-0.5',
        textSize: 'text-[8px]',
        timerSize: 'text-[7px]',
        iconSize: 'w-2 h-2',
        labelSize: 'text-[8px]',
        headerTextSize: 'text-[11px]',
        operatoryLabelSize: 'text-[8px]'
      };
    }
  };

  const sizing = getSizing(opCount);

  return (
    <div 
      className="fixed top-2 left-1/2 transform -translate-x-1/2 z-[100]"
      data-testid="floating-widget"
    >
      <div 
        className="backdrop-blur-xl border shadow-2xl rounded-2xl overflow-hidden bg-slate-800/95 border-slate-700/50 text-white" 
        style={{ width: sizing.widgetWidth, maxWidth: '95vw' }}
      >
        {/* Header Row - Provider name left, Actions right */}
        <div className="px-3 py-2 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            {/* Provider Name - Left side */}
            <h3 className={`font-heading font-bold ${sizing.headerTextSize} text-white`}>
              {loading ? 'Loading...' : providerName}
            </h3>
            
            {/* Actions - Right side (always visible) */}
            <div className="flex items-center gap-1.5">
              {/* Message Button */}
              <button
                onClick={() => navigate('/messaging')}
                data-testid="widget-messaging-button"
                className="p-1 rounded-lg transition-colors hover:bg-slate-700/50 text-dp-400 hover:text-dp-300"
                title="Messages"
              >
                <MessageCircle className="w-4 h-4" />
              </button>
              
              {/* Close Button */}
              <button
                onClick={onClose}
                data-testid="close-widget-button"
                className="p-1 rounded-lg transition-colors hover:bg-slate-700/50 text-slate-400 hover:text-slate-300"
                title="Minimize widget"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Buttons Area - Operatory labels above each set of buttons */}
        <div className="px-6 py-2">
          {loading ? (
            <div className="text-center py-2 text-sm text-slate-400">
              Loading...
            </div>
          ) : providerOperatories.length === 0 ? (
            <div className="text-center py-2 text-sm text-slate-400">
              No operatories assigned
            </div>
          ) : (
            <div className="flex justify-center items-end gap-3">
              {(() => {
                // Group operatories by provider
                const uniqueProviders = [...new Set(providerOperatories.map(op => op.current_provider_id).filter(Boolean))];
                const showProviderLabels = uniqueProviders.length > 1;

                if (!showProviderLabels) {
                  // Single provider — no sub-labels needed
                  return providerOperatories.map((op, index) => (
                    <React.Fragment key={op.id}>
                      <OperatoryColumn 
                        operatory={op}
                        practice={practice}
                        fetchOperatories={fetchOperatories}
                        sizing={sizing}
                      />
                      {index < providerOperatories.length - 1 && (
                        <div className="flex flex-col justify-end h-full">
                          <div className="w-px bg-slate-600/50" style={{ height: '24px' }} />
                        </div>
                      )}
                    </React.Fragment>
                  ));
                }

                // Multiple providers — group and label
                const groups = [];
                let currentProviderId = null;
                providerOperatories.forEach(op => {
                  if (op.current_provider_id !== currentProviderId) {
                    groups.push({ providerId: op.current_provider_id, name: op.current_provider_name, ops: [op] });
                    currentProviderId = op.current_provider_id;
                  } else {
                    groups[groups.length - 1].ops.push(op);
                  }
                });

                return groups.map((group, gIndex) => (
                  <React.Fragment key={group.providerId || gIndex}>
                    {gIndex > 0 && (
                      <div className="flex flex-col justify-end h-full">
                        <div className="w-px bg-slate-500/70" style={{ height: '100%' }} />
                      </div>
                    )}
                    <div className="flex flex-col items-center">
                      <span className={`${sizing.operatoryLabelSize} font-semibold text-dp-400 mb-1 whitespace-nowrap`}>
                        {group.name}
                      </span>
                      <div className="flex items-end gap-3">
                        {group.ops.map((op, oIndex) => (
                          <React.Fragment key={op.id}>
                            <OperatoryColumn 
                              operatory={op}
                              practice={practice}
                              fetchOperatories={fetchOperatories}
                              sizing={sizing}
                            />
                            {oIndex < group.ops.length - 1 && (
                              <div className="flex flex-col justify-end h-full">
                                <div className="w-px bg-slate-600/50" style={{ height: '24px' }} />
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </React.Fragment>
                ));
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
